import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader, { MobileSearchBar } from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilterPanel from "@/components/feature/FilterPanel";
import ResourceCard from "@/components/base/ResourceCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { resources } from "@/mocks/resources";
import type { FilterState, SortKey } from "@/types/content";
import {
  EMPTY_FILTER,
  PAGE_SIZE,
  filterResources,
  getHotResources,
  paginate,
  searchResources,
  sortResources,
  totalPages,
} from "@/utils/content";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "时间最新" },
  { key: "views", label: "点击最多" },
  { key: "rating", label: "评分优先" },
];

const filterOptions = {
  types: Array.from(new Set(resources.map((item) => item.type))),
  regions: Array.from(new Set(resources.map((item) => item.region))),
  years: Array.from(new Set(resources.map((item) => item.year))).sort((a, b) => b.localeCompare(a)),
  tags: Array.from(new Set(resources.flatMap((item) => item.tags))),
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useDocumentMeta(
    keyword ? `搜索“${keyword}” - 无码资源搜索结果` : "全站资源搜索 - 无码高清资源平台",
    keyword
      ? `显示与“${keyword}”匹配的无码资源及最新资讯，助你快速查找所需内容。`
      : "输入关键词即可检索无码站内的影视、动漫、音乐资源，支持多维筛选与排序。",
  );

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  const baseResults = useMemo(() => searchResources(keyword), [keyword]);
  const results = useMemo(
    () => sortResources(filterResources(baseResults, filter), sort),
    [baseResults, filter, sort],
  );

  const pages = totalPages(results.length);
  const currentPage = Math.min(page, pages);
  const visible = paginate(results, currentPage);
  const hot = getHotResources(4);
  const activeCount = [filter.type, filter.region, filter.year, filter.tag].filter(Boolean).length;

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  const resetFilter = () => {
    setFilter(EMPTY_FILTER);
    setSort("latest");
    setPage(1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 pb-24 md:pb-0">
        <div className="wrap pt-5 md:pt-7">
          <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to="/" className="cursor-pointer hover:text-primary-600 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <span className="text-foreground-700">搜索</span>
          </nav>

          <header className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
            <h1 className="font-heading font-extrabold text-xl md:text-3xl text-foreground-950">
              {keyword ? (
                <>
                  搜索“<span className="text-primary-600">{keyword}</span>”
                </>
              ) : (
                "全站资源搜索"
              )}
            </h1>
            <p className="mt-2 text-sm text-foreground-600 leading-6">
              支持资源标题、简介描述与标签匹配，输入关键词后按回车即可检索全站内容。
            </p>
            <form
              className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl"
              onSubmit={(event) => {
                event.preventDefault();
                submit(input);
              }}
              role="search"
            >
              <div className="relative flex-1 min-w-0">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  name="q"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="例如：科幻、星轨、城市流行"
                  aria-label="搜索关键词"
                  className="w-full h-11 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-foreground-500">
              <span>试试：</span>
              {["科幻", "星轨", "城市流行", "治愈"].map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => submit(word)}
                  className="chip bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 cursor-pointer transition-colors"
                >
                  {word}
                </button>
              ))}
            </div>
          </header>

          <div className="mt-5 md:mt-7 grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-5 md:gap-7 items-start">
            <aside className="lg:sticky lg:top-24">
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="lg:hidden w-full h-11 mb-3 rounded-md border border-background-300 bg-background-50 flex items-center justify-between px-4 text-sm text-foreground-700 cursor-pointer"
                aria-expanded={filterOpen}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500" aria-hidden="true"></i>
                  结果筛选
                  {activeCount > 0 && <span className="chip bg-primary-500 text-background-50">{activeCount}</span>}
                </span>
                <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
              </button>
              <div className={`${filterOpen ? "block" : "hidden"} lg:block`}>
                <FilterPanel
                  options={filterOptions}
                  value={filter}
                  onChange={(next) => {
                    setFilter(next);
                    setPage(1);
                  }}
                  onReset={resetFilter}
                />
              </div>
            </aside>

            <section aria-label="搜索结果列表">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <p className="text-xs md:text-sm text-foreground-500">
                  {keyword ? (
                    <>
                      与“<span className="text-foreground-800">{keyword}</span>”匹配的结果共{" "}
                      <span className="text-primary-600 font-semibold">{results.length}</span> 条
                    </>
                  ) : (
                    "输入关键词开始检索"
                  )}
                  <span className="ml-2 text-foreground-400">（前端演示结果）</span>
                </p>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 self-start sm:self-auto">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => {
                        setSort(option.key);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                        sort === option.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {visible.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-5">
                    {visible.map((item) => (
                      <ResourceCard key={item.id} resource={item} />
                    ))}
                  </div>
                  <div className="mt-8">
                    <Pagination page={currentPage} total={pages} onChange={setPage} />
                    <p className="mt-3 text-center text-xs text-foreground-400">
                      第 {currentPage} / {pages} 页 · 每页 {PAGE_SIZE} 条
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <div className="bg-background-50 border border-background-200 rounded-lg py-12 px-6 text-center">
                    <span className="mx-auto w-14 h-14 rounded-md bg-background-100 text-foreground-400 flex items-center justify-center">
                      <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
                    </span>
                    <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                      {keyword ? `没有与“${keyword}”匹配的结果` : "还没有输入搜索关键词"}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-600 leading-6 max-w-lg mx-auto">
                      {keyword
                        ? "可以尝试更换更短的词，或者减少筛选条件。也可以直接从下方热门内容开始浏览。"
                        : "在上方输入资源名称、题材标签或地区年份，即可快速定位站内内容。"}
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-2">
                      <button
                        type="button"
                        onClick={resetFilter}
                        className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
                      >
                        重置筛选条件
                      </button>
                      <Link
                        to="/"
                        className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 hover:border-primary-300 text-sm flex items-center cursor-pointer transition-colors whitespace-nowrap"
                      >
                        返回首页
                      </Link>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="flex items-end justify-between gap-4 mb-4">
                      <h2 className="font-heading font-bold text-lg text-foreground-950">热门内容推荐</h2>
                      <Link
                        to="/category/movie"
                        className="text-xs text-foreground-500 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                      >
                        浏览全部分类
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
                      {hot.map((item) => (
                        <ResourceCard key={item.id} resource={item} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
      <MobileSearchBar />
      <SiteFooter />
    </div>
  );
}